import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth, api } from '../App';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { toast } from 'sonner';
import { ArrowLeft, Loader2, User, Building2, Save } from 'lucide-react';

const NICHES = ['Fashion', 'Fitness', 'Tech', 'Beauty', 'Food', 'Travel', 'Lifestyle', 'Gaming', 'Music', 'Education'];
const PLATFORMS = ['Instagram', 'TikTok', 'YouTube', 'Twitter', 'LinkedIn', 'Facebook'];
const FOLLOWER_RANGES = ['Below 1k', '1k-5k', '5k-10k', '10k-25k', '25k-50k', '50k-100k', '100k-250k', '250k-500k', '500k+'];
const BUDGET_RANGES = ['Gifted', 'Under $50', '$50-$100', '$100-$250', '$250-$500', '$500-$1000', '$1000-$5000', '$5000+'];
const INDUSTRIES = ['Fashion', 'Tech', 'Food & Beverage', 'Health & Wellness', 'Beauty', 'Travel', 'Finance', 'Education', 'Entertainment', 'Other'];

const Settings = () => {
  const navigate = useNavigate();
  const { user, updateUser, logout } = useAuth();
  
  const [loading, setLoading] = useState(false);
  const [name, setName] = useState(user?.name || '');
  
  // Creator profile
  const [creatorData, setCreatorData] = useState({
    niches: user?.creator_profile?.niches || [],
    follower_range: user?.creator_profile?.follower_range || '',
    platforms: user?.creator_profile?.platforms || [],
    bio: user?.creator_profile?.bio || '',
    portfolio_url: user?.creator_profile?.portfolio_url || '',
    instagram_handle: user?.creator_profile?.instagram_handle || '',
    tiktok_handle: user?.creator_profile?.tiktok_handle || '',
    youtube_handle: user?.creator_profile?.youtube_handle || '',
    location: user?.creator_profile?.location || '',
    rate_per_post: user?.creator_profile?.rate_per_post || 0
  });

  // Brand profile
  const [brandData, setBrandData] = useState({
    company_name: user?.brand_profile?.company_name || '',
    industry: user?.brand_profile?.industry || '',
    budget_range: user?.brand_profile?.budget_range || '',
    website: user?.brand_profile?.website || '',
    description: user?.brand_profile?.description || '',
    logo_url: user?.brand_profile?.logo_url || '',
    location: user?.brand_profile?.location || ''
  });

  const isCreator = user?.role === 'creator';
  const dashboardPath = isCreator ? '/creator' : '/brand';

  const handleNicheToggle = (niche) => {
    setCreatorData(prev => ({
      ...prev,
      niches: prev.niches.includes(niche)
        ? prev.niches.filter(n => n !== niche)
        : [...prev.niches, niche]
    }));
  };

  const handlePlatformToggle = (platform) => {
    setCreatorData(prev => ({
      ...prev,
      platforms: prev.platforms.includes(platform)
        ? prev.platforms.filter(p => p !== platform)
        : [...prev.platforms, platform]
    }));
  };

  const handleSave = async () => {
    setLoading(true);
    try {
      const updateData = {
        name,
        ...(isCreator 
          ? { creator_profile: creatorData }
          : { brand_profile: brandData }
        )
      };
      
      const response = await api.put('/users/profile', updateData);
      updateUser(response.data);
      toast.success('Profile updated!');
    } catch (error) {
      toast.error('Failed to update profile');
    } finally {
      setLoading(false);
    }
  };

  const handleLogout = async () => {
    await logout();
    navigate('/');
  };

  return (
    <div className="min-h-screen bg-background">
      <div className="max-w-2xl mx-auto px-6 py-8">
        {/* Header */}
        <div className="flex items-center justify-between mb-8">
          <div className="flex items-center gap-4">
            <button 
              onClick={() => navigate(dashboardPath)}
              className="flex items-center gap-2 text-muted-foreground hover:text-foreground"
              data-testid="back-btn"
            >
              <ArrowLeft className="w-5 h-5" />
            </button>
            <h1 className="text-2xl font-bold" style={{ fontFamily: 'Plus Jakarta Sans' }}>
              Settings
            </h1>
          </div>
        </div>

        {/* Profile Picture & Name */}
        <Card className="mb-6">
          <CardHeader>
            <CardTitle className="text-lg">Profile</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="flex items-center gap-4">
              <div className={`w-16 h-16 rounded-full flex items-center justify-center overflow-hidden ${
                isCreator ? 'bg-[#E8795A]/20' : 'bg-[#3AAFB9]/20'
              }`}>
                {user?.picture ? (
                  <img src={user.picture} alt="" className="w-full h-full object-cover" />
                ) : isCreator ? (
                  <User className="w-8 h-8 text-[#E8795A]" />
                ) : (
                  <Building2 className="w-8 h-8 text-[#3AAFB9]" />
                )}
              </div>
              <div>
                <p className="font-medium">{user?.name}</p>
                <p className="text-sm text-muted-foreground">{user?.email}</p>
              </div>
            </div>
            
            <div>
              <Label className="text-sm font-medium mb-2 block">Display Name</Label>
              <Input
                value={name}
                onChange={(e) => setName(e.target.value)}
                data-testid="name-input"
              />
            </div>
          </CardContent>
        </Card>

        {/* Creator Settings */}
        {isCreator && (
          <>
            <Card className="mb-6">
              <CardHeader>
                <CardTitle className="text-lg">Creator Profile</CardTitle>
              </CardHeader>
              <CardContent className="space-y-6">
                {/* Niches */}
                <div>
                  <Label className="text-sm font-medium mb-3 block">Your Niches</Label>
                  <div className="flex flex-wrap gap-2">
                    {NICHES.map(niche => (
                      <button
                        key={niche}
                        onClick={() => handleNicheToggle(niche)}
                        className={`px-4 py-2 rounded-full text-sm transition-all ${
                          creatorData.niches.includes(niche)
                            ? 'bg-[#E8795A] text-background'
                            : 'bg-secondary hover:bg-secondary/80'
                        }`}
                      >
                        {niche}
                      </button>
                    ))}
                  </div>
                </div>

                {/* Follower Range */}
                <div>
                  <Label className="text-sm font-medium mb-2 block">Follower Count</Label>
                  <Select 
                    value={creatorData.follower_range} 
                    onValueChange={(v) => setCreatorData(prev => ({ ...prev, follower_range: v }))}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Select range" />
                    </SelectTrigger>
                    <SelectContent>
                      {FOLLOWER_RANGES.map(range => (
                        <SelectItem key={range} value={range}>{range}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                {/* Platforms */}
                <div>
                  <Label className="text-sm font-medium mb-3 block">Platforms</Label>
                  <div className="flex flex-wrap gap-2">
                    {PLATFORMS.map(platform => (
                      <button
                        key={platform}
                        onClick={() => handlePlatformToggle(platform)}
                        className={`px-4 py-2 rounded-full text-sm transition-all ${
                          creatorData.platforms.includes(platform)
                            ? 'bg-primary text-primary-foreground'
                            : 'bg-secondary hover:bg-secondary/80'
                        }`}
                      >
                        {platform}
                      </button>
                    ))}
                  </div>
                </div>

                {/* Bio */}
                <div>
                  <Label className="text-sm font-medium mb-2 block">Bio</Label>
                  <Textarea
                    placeholder="Tell brands about yourself..."
                    value={creatorData.bio}
                    onChange={(e) => setCreatorData(prev => ({ ...prev, bio: e.target.value }))}
                    rows={4}
                  />
                </div>

                {/* Rate */}
                <div>
                  <Label className="text-sm font-medium mb-2 block">Rate per Post ($)</Label>
                  <Input
                    type="number"
                    value={creatorData.rate_per_post || ''}
                    onChange={(e) => setCreatorData(prev => ({ ...prev, rate_per_post: parseFloat(e.target.value) || 0 }))}
                  />
                </div>

                {/* Location */}
                <div>
                  <Label className="text-sm font-medium mb-2 block">Location</Label>
                  <Input
                    placeholder="City, Country"
                    value={creatorData.location}
                    onChange={(e) => setCreatorData(prev => ({ ...prev, location: e.target.value }))}
                  />
                </div>

                {/* Social Handles */}
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label className="text-sm font-medium mb-2 block">Instagram</Label>
                    <Input
                      placeholder="@username"
                      value={creatorData.instagram_handle}
                      onChange={(e) => setCreatorData(prev => ({ ...prev, instagram_handle: e.target.value }))}
                    />
                  </div>
                  <div>
                    <Label className="text-sm font-medium mb-2 block">YouTube</Label>
                    <Input
                      placeholder="Channel name"
                      value={creatorData.youtube_handle}
                      onChange={(e) => setCreatorData(prev => ({ ...prev, youtube_handle: e.target.value }))}
                    />
                  </div>
                </div>

                <div>
                  <Label className="text-sm font-medium mb-2 block">Portfolio URL</Label>
                  <Input
                    type="url"
                    placeholder="https://yourportfolio.com"
                    value={creatorData.portfolio_url}
                    onChange={(e) => setCreatorData(prev => ({ ...prev, portfolio_url: e.target.value }))}
                  />
                </div>
              </CardContent>
            </Card>
          </>
        )}

        {/* Brand Settings */}
        {!isCreator && (
          <>
            <Card className="mb-6">
              <CardHeader>
                <CardTitle className="text-lg">Brand Profile</CardTitle>
              </CardHeader>
              <CardContent className="space-y-6">
                <div>
                  <Label className="text-sm font-medium mb-2 block">Company Name</Label>
                  <Input
                    value={brandData.company_name}
                    onChange={(e) => setBrandData(prev => ({ ...prev, company_name: e.target.value }))}
                  />
                </div>

                <div>
                  <Label className="text-sm font-medium mb-2 block">Industry</Label>
                  <Select 
                    value={brandData.industry} 
                    onValueChange={(v) => setBrandData(prev => ({ ...prev, industry: v }))}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Select industry" />
                    </SelectTrigger>
                    <SelectContent>
                      {INDUSTRIES.map(ind => (
                        <SelectItem key={ind} value={ind}>{ind}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                <div>
                  <Label className="text-sm font-medium mb-2 block">Typical Budget</Label>
                  <Select 
                    value={brandData.budget_range} 
                    onValueChange={(v) => setBrandData(prev => ({ ...prev, budget_range: v }))}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Select budget range" />
                    </SelectTrigger>
                    <SelectContent>
                      {BUDGET_RANGES.map(range => (
                        <SelectItem key={range} value={range}>{range}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                <div>
                  <Label className="text-sm font-medium mb-2 block">Website</Label>
                  <Input
                    type="url"
                    placeholder="https://yourcompany.com"
                    value={brandData.website}
                    onChange={(e) => setBrandData(prev => ({ ...prev, website: e.target.value }))}
                  />
                </div>

                <div>
                  <Label className="text-sm font-medium mb-2 block">Logo URL</Label>
                  <Input
                    type="url"
                    placeholder="https://yourcompany.com/logo.png"
                    value={brandData.logo_url}
                    onChange={(e) => setBrandData(prev => ({ ...prev, logo_url: e.target.value }))}
                  />
                </div>

                <div>
                  <Label className="text-sm font-medium mb-2 block">Description</Label>
                  <Textarea
                    placeholder="Tell creators about your brand..."
                    value={brandData.description}
                    onChange={(e) => setBrandData(prev => ({ ...prev, description: e.target.value }))}
                    rows={4}
                  />
                </div>

                <div>
                  <Label className="text-sm font-medium mb-2 block">Location</Label>
                  <Input
                    placeholder="City, Country"
                    value={brandData.location}
                    onChange={(e) => setBrandData(prev => ({ ...prev, location: e.target.value }))}
                  />
                </div>
              </CardContent>
            </Card>
          </>
        )}

        {/* Actions */}
        <div className="flex flex-col gap-4">
          <Button 
            onClick={handleSave}
            disabled={loading}
            className="w-full"
            data-testid="save-btn"
          >
            {loading ? (
              <Loader2 className="w-4 h-4 animate-spin mr-2" />
            ) : (
              <Save className="w-4 h-4 mr-2" />
            )}
            Save Changes
          </Button>
          
          <Button 
            variant="outline"
            onClick={handleLogout}
            className="w-full text-destructive hover:text-destructive"
            data-testid="logout-btn"
          >
            Log out
          </Button>
        </div>
      </div>
    </div>
  );
};

export default Settings;
